import {computed, type ComputedRef, defineComponent, type PropType, ref, watch} from 'vue';
import {
    AthumiService, type ISearchSessionsQuery,
    PaymentMethod,
    SearchSessionsQuery,
    SessionState,
    SessionType
} from "@/api/enterprise-gateway-api.proxy.ts";
import {debounce} from "lodash";
import {type EnumLabelMap, enumToSelectOptions} from "@/app/shared/helpers/enum.ts";
import {InputText, Select} from 'primevue';
import DatePicker from 'primevue/datepicker';
import DateUtil from '@/app/shared/helpers/date-util.ts';
import {getAvailableServices} from "@/app/shared/helpers/services.ts";

export const sessionTypeLabels: EnumLabelMap = {
    Payment: 'Directe betaling',
    TopUp: 'Opladen service-saldo',
    PostPaid: 'Postpaid transactie',
    SelfBilling: 'Selfbilling transactie',
};

export const paymentMethodLabels: EnumLabelMap = {
    Online: 'Online betaling via PSP',
    Credits: 'Service-saldo betaling',
    Invoice: 'Achteraf op factuur',
    InvoiceFedNot: 'Schuldverlegging via Fednot'
};

export default defineComponent({
    name: 'AegSearchSession',
    components: { InputText, DatePicker, Select },
    emits: ['search'],
    props: {
        defaultQuery: {
            type: Object as PropType<ISearchSessionsQuery>,
            required: false
        },
    },
    setup: function (props, {emit}) {
        const watchFields: ComputedRef<SearchSessionsQuery> = computed(() => {
            return SearchSessionsQuery.fromJS({
                transactionId: transactionId.value,
                fromDate: DateUtil.formatForApi(dateFrom.value),
                toDate: DateUtil.formatForApi(dateTo.value),
                state: selectedState.value,
                paymentMethod: selectedPaymentMethod.value,
                type: selectedSessionType.value,
                service: selectedServiceType.value,
                actorIdentifier: actorIdentifier.value,
                actorName: actorName.value,
                sessionDetail: sessionDetail.value,
                documentNumber: documentNumber.value
            });
        });


        const transactionId = ref<string | undefined>(undefined);
        const dateFrom = ref<string | undefined>(undefined);
        const dateTo = ref<string | undefined>(undefined);

        const sessionStateOptions = enumToSelectOptions(SessionState);
        const selectedState = ref<SessionState | undefined>(undefined);

        const sessionTypeOptions = enumToSelectOptions(SessionType, sessionTypeLabels);
        const selectedSessionType = ref<SessionType | undefined>(undefined);

        const paymentMethodOptions = enumToSelectOptions(PaymentMethod, paymentMethodLabels);
        const selectedPaymentMethod = ref<PaymentMethod | undefined>(undefined);

        const serviceTypeOptions = getAvailableServices();
        const selectedServiceType = ref<AthumiService | undefined>(undefined);

        const actorIdentifier = ref<string | undefined>(undefined);
        const actorName = ref<string | undefined>(undefined);
        const sessionDetail = ref<string | undefined>(undefined);
        const documentNumber = ref<string | undefined>(undefined);

        const debouncedSearch = debounce(async () => {
            emit('search', watchFields.value);
        }, 100);

        const searchFieldChanged = () => {
            debouncedSearch();
        }

        watch(watchFields, searchFieldChanged, {immediate: false, deep: true});

        watch(() => props.defaultQuery, (newDefaultQuery) => {
            if (newDefaultQuery) {
                transactionId.value = newDefaultQuery.transactionId?.trim();
                dateFrom.value = newDefaultQuery.fromDate;
                dateTo.value = newDefaultQuery.toDate;
                selectedState.value = newDefaultQuery.state;
                selectedSessionType.value = newDefaultQuery.type;
                selectedServiceType.value = newDefaultQuery.service;
                selectedPaymentMethod.value = newDefaultQuery.paymentMethod;
                actorIdentifier.value = newDefaultQuery.actorIdentifier?.trim();
                actorName.value = newDefaultQuery.actorName?.trim();
                sessionDetail.value = newDefaultQuery.sessionDetail?.trim();
                documentNumber.value = newDefaultQuery.documentNumber?.trim();
            }
        }, {immediate: true, deep: true});


        return {
            transactionId,
            dateFrom,
            dateTo,
            sessionStateOptions,
            sessionTypeOptions,
            serviceTypeOptions,
            paymentMethodOptions,
            selectedState,
            selectedSessionType,
            selectedServiceType,
            selectedPaymentMethod,
            actorName,
            sessionDetail,
            documentNumber,
            actorIdentifier
        };
    }
})
